import { ConfigHelper } from '../../base/constants'

/**
 * Sürüklenebilir B-Gini logosunun saf mantığı (spec §5). DOM'a dokunmaz; yalnız `readStoredRatio`
 * ve `storeRatio` `localStorage`'a gider ve her erişim `try/catch` içindedir.
 */

/** Bu mesafenin altındaki hareket tık sayılır; eşik ve üstü sürüklemedir. */
export const DRAG_THRESHOLD_PX = 5

/** FAB'in bugünkü boyutu (`.fab { width: 56px; height: 56px }`). */
export const FAB_SIZE_PX = 56

/** Logonun viewport kenarına en fazla yaklaşabileceği mesafe. */
export const FAB_EDGE_MARGIN_PX = 8

/** `Alt + ok` tuşunun bir basışta kaydırdığı mesafe. */
export const KEYBOARD_STEP_PX = 16

/** Viewport içindeki sol-üst köşe oranı (0–1). Pencere boyutu değişse de göreli yer korunur. */
export interface FabRatio {
  x: number
  y: number
}

export interface FabPx {
  left: number
  top: number
}

export type FabSideX = 'left' | 'right'
export type FabSideY = 'top' | 'bottom'

export function isDrag(dx: number, dy: number): boolean {
  return Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX
}

function clampAxis(value: number, size: number, viewport: number, margin: number): number {
  const max = viewport - size - margin
  // Viewport logodan darsa sol/üst kenar payına yaslanır.
  if (max < margin) return margin
  return Math.min(Math.max(value, margin), max)
}

export function clampPx(
  left: number,
  top: number,
  size: number,
  vw: number,
  vh: number,
  margin: number = FAB_EDGE_MARGIN_PX
): FabPx {
  return { left: clampAxis(left, size, vw, margin), top: clampAxis(top, size, vh, margin) }
}

const clamp01 = (value: number) => Math.min(Math.max(value, 0), 1)

/** Oran, logonun gidebileceği aralığa (`viewport - size`) göredir: sağ kenardaki logo 1'dir. */
export function toRatio(left: number, top: number, size: number, vw: number, vh: number): FabRatio {
  const rangeX = Math.max(vw - size, 1)
  const rangeY = Math.max(vh - size, 1)
  return { x: clamp01(left / rangeX), y: clamp01(top / rangeY) }
}

export function fromRatio(r: FabRatio, size: number, vw: number, vh: number): FabPx {
  const left = Math.round(r.x * Math.max(vw - size, 0))
  const top = Math.round(r.y * Math.max(vh - size, 0))
  return clampPx(left, top, size, vw, vh)
}

/** Logonun merkezinin bulunduğu yarı: tooltip ve baloncuklar karşı yöne açılır. */
export function sides(left: number, top: number, size: number, vw: number, vh: number): { x: FabSideX; y: FabSideY } {
  const centerX = left + size / 2
  const centerY = top + size / 2
  return { x: centerX < vw / 2 ? 'left' : 'right', y: centerY < vh / 2 ? 'top' : 'bottom' }
}

/** `Alt + ok` kaydırması; ok dışındaki tuşlar `null`. */
export function keyboardStep(key: string): { dx: number; dy: number } | null {
  switch (key) {
    case 'ArrowLeft':
      return { dx: -KEYBOARD_STEP_PX, dy: 0 }
    case 'ArrowRight':
      return { dx: KEYBOARD_STEP_PX, dy: 0 }
    case 'ArrowUp':
      return { dx: 0, dy: -KEYBOARD_STEP_PX }
    case 'ArrowDown':
      return { dx: 0, dy: KEYBOARD_STEP_PX }
    default:
      return null
  }
}

const isUnitNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1

/** Kayıtlı oranı okur; kayıt yok, bozuk ya da depolamaya erişilemiyorsa `null`. */
export function readStoredRatio(): FabRatio | null {
  try {
    const raw = window.localStorage.getItem(ConfigHelper.INDEXPAZAR_GINI_FAB_POSITION)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const { x, y } = parsed as Record<string, unknown>
    return isUnitNumber(x) && isUnitNumber(y) ? { x, y } : null
  } catch {
    return null
  }
}

/** Oranı saklar; depolama dolu/kapalıysa sessizce geçer (logo yine bu oturumda yerinde kalır). */
export function storeRatio(r: FabRatio): void {
  try {
    window.localStorage.setItem(ConfigHelper.INDEXPAZAR_GINI_FAB_POSITION, JSON.stringify({ x: r.x, y: r.y }))
  } catch {
    /* depolama erişilemez: yok say */
  }
}
